/**
 * Mapping helpers between the frontend AppEvent model and the backend REST
 * contracts at /api/v1/events.
 *
 * Deferred / out of scope for this integration:
 *   - poster / logo / flyer images  → backend uses S3 keys, uploaded separately
 */
import type { CreateEventBody, UpdateEventBody } from '@proaxiv/server-api'
import type { AppEvent } from '../../store/eventsSlice'
import { DEFAULT_BRANDING } from '../../store/eventsSlice'

// ── Backend response shapes ───────────────────────────────────────────────────
// The generated client types these as `object` because Springdoc does not
// inline the response schemas. We define them manually here from the Java
// record fields.

/** Backend DTO returned by GET /api/v1/events/:id and the list endpoint. */
export type EventSummary = {
  id: string
  slug?: string
  title?: string
  description?: string
  startDate?: string
  endDate?: string
  venue?: string
  venueMapUrl?: string
  status?: 'DRAFT' | 'PUBLISHED' | 'COMPLETED'
  logoS3Key?: string
  primaryColor?: string
  fontFamily?: string
  flyerS3Key?: string
  qrActivationDate?: string
  reminderAt?: string
  registrationOpen?: boolean
  registrationClosesAt?: string
  createdAt?: string
  createdByUserId?: string
  ticketAnnouncement?: string
  backgroundColor?: string
  textColor?: string
  accentColor?: string
  headingFontFamily?: string
  /** Raw stored override: OPEN/CLOSED/HIDDEN, undefined=Auto (never set) */
  registrationManualState?: 'OPEN' | 'CLOSED' | 'HIDDEN'
  /** IANA timezone ID (e.g. "Europe/Rome") used to interpret the event's wall-clock times */
  timezone?: string
}

/** Backend DTO returned by GET /api/v1/events (paginated list). */
export type ListEventsResponse = {
  success: boolean
  events: EventSummary[]
  total: number
  errorCode?: string
  message?: string
}

/** Backend DTO returned by GET /api/v1/events/:id (single event). */
export type GetEventResponse = {
  success: boolean
  event: EventSummary
  errorCode?: string
  message?: string
}

/**
 * Backend DTO returned by GET /api/v1/public/events/:id.
 * Extends the standard response with {@link draftPreview}, which is {@code true}
 * when the event is DRAFT and the authenticated caller is an org member.
 */
export type GetPublicEventResponse = GetEventResponse & {
  /** When true, the event is a DRAFT visible to an authenticated org member. */
  draftPreview?: boolean
}

/** Backend DTO returned by POST /api/v1/events (create). */
export type CreateEventResponse = {
  success: boolean
  eventId?: string
  slug?: string
  errorCode?: string
  message?: string
}

// ── Frontend → Backend ────────────────────────────────────────────────────────

/**
 * Maps a draft AppEvent payload to the CreateEventBody the backend expects.
 * @param payload   The form data from EventEditorPage (no id/createdAt yet)
 * @param orgId     Organisation the caller belongs to (from JWT org_ids claim)
 */
export function toCreateEventBody(
  payload: Omit<AppEvent, 'id' | 'createdAt'>,
  orgId: string
): CreateEventBody {
  return {
    orgId,
    title: payload.title,
    description: payload.text || undefined,
    startDate: payload.startDateTime || undefined,
    endDate: payload.endDateTime || undefined,
    venue: payload.address || undefined,
    primaryColor: payload.branding.primary || undefined,
    fontFamily: payload.branding.bodyFont || undefined,
    qrActivationDate: payload.ticketActivation || undefined,
    reminderAt: payload.autoReminderAt || undefined,
    registrationClosesAt: payload.registrationClosesAt || undefined,
    ticketAnnouncement: payload.ticketAnnouncement || undefined,
    backgroundColor: payload.branding.background || undefined,
    textColor: payload.branding.text || undefined,
    accentColor: payload.branding.accent || undefined,
    headingFontFamily: payload.branding.headingFont || undefined,
    timezone: payload.timezone || undefined,
    // venueMapUrl — not collected by the editor form; omitted
  }
}

/**
 * Maps a draft AppEvent payload to the UpdateEventBody the backend expects.
 */
export function toUpdateEventBody(payload: Omit<AppEvent, 'id' | 'createdAt'>): UpdateEventBody {
  return {
    title: payload.title,
    // The backend treats an omitted (null/undefined) field as "leave unchanged" (see
    // UpdateEventRequest's Javadoc) — mapping an emptied text field to `undefined` here would
    // silently drop the clear instead of persisting it, leaving the stale value on refresh.
    // These are always sent as their literal current value (including ''), never `undefined`.
    description: payload.text,
    startDate: payload.startDateTime || undefined,
    endDate: payload.endDateTime || undefined,
    venue: payload.address,
    status: payload.status === 'published' ? 'PUBLISHED' : 'DRAFT',
    primaryColor: payload.branding.primary || undefined,
    fontFamily: payload.branding.bodyFont || undefined,
    qrActivationDate: payload.ticketActivation || undefined,
    // The backend treats an omitted qrActivationDate as "leave unchanged", so clearing the
    // field client-side must be signalled explicitly rather than sending nothing.
    clearQrActivationDate: payload.ticketActivation ? undefined : true,
    reminderAt: payload.autoReminderAt || undefined,
    // AUTO clears a prior OPEN/CLOSED/HIDDEN override so the deadline applies again.
    // Omitting the field would leave the previous override unchanged on the backend.
    registrationManualState:
      payload.registrationManualState === 'open'
        ? 'OPEN'
        : payload.registrationManualState === 'closed'
          ? 'CLOSED'
          : payload.registrationManualState === 'hidden'
            ? 'HIDDEN'
            : 'AUTO',
    registrationClosesAt: payload.registrationClosesAt || undefined,
    // Same "empty means clear, not unchanged" reasoning as description/venue above.
    ticketAnnouncement: payload.ticketAnnouncement ?? '',
    backgroundColor: payload.branding.background || undefined,
    textColor: payload.branding.text || undefined,
    accentColor: payload.branding.accent || undefined,
    headingFontFamily: payload.branding.headingFont || undefined,
    timezone: payload.timezone || undefined,
  }
}

// ── Backend → Frontend ────────────────────────────────────────────────────────

/**
 * Converts an S3 key to a server-side URL UploadedFile stub.
 * The `dataUrl` field is intentionally a regular URL path — <img> tags accept both
 * data URLs and regular URLs. uploadEventImage skips re-upload when it detects this.
 *
 * The asset path itself (/api/v1/public/events/{id}/{type}) never changes, so a browser or CDN
 * that already cached one response would keep serving it forever after a replace/remove. `s3Key`
 * changes on every upload (see EventModuleImpl.uploadEventLogo/uploadEventFlyer) and removal
 * clears it entirely, so appending it as a `v` query param busts the cache whenever the
 * underlying asset actually changes.
 */
function assetUrl(
  eventId: string,
  type: 'logo' | 'flyer',
  s3Key?: string
): import('../../store/eventsSlice').UploadedFile {
  const base = `/api/v1/public/events/${eventId}/${type}`
  return {
    name: type,
    dataUrl: s3Key ? `${base}?v=${encodeURIComponent(s3Key)}` : base,
    mimeType: 'image/*',
  }
}

/**
 * Maps a backend EventSummary to an AppEvent shape for use in the UI.
 * Fields with no backend equivalent (poster, ticketAnnouncement, etc.) are
 * set to safe defaults so the UI doesn't break.
 *
 * @param summary   The backend event summary DTO.
 * @param draftPreview  When true, marks the resulting AppEvent as a draft preview
 *                      visible only to authenticated org members.
 */
export function fromEventSummary(summary: EventSummary, draftPreview?: boolean): AppEvent {
  return {
    id: summary.id,
    title: summary.title ?? '',
    status: summary.status === 'PUBLISHED' ? 'published' : 'draft',
    address: summary.venue ?? '',
    startDateTime: summary.startDate ?? '',
    endDateTime: summary.endDate,
    text: summary.description ?? '',
    ticketAnnouncement: summary.ticketAnnouncement || undefined,
    poster: summary.flyerS3Key ? assetUrl(summary.id, 'flyer', summary.flyerS3Key) : undefined,
    ticketActivation: summary.qrActivationDate ?? '',
    registrationClosesAt: summary.registrationClosesAt,
    registrationManualState:
      summary.registrationManualState === 'OPEN'
        ? 'open'
        : summary.registrationManualState === 'CLOSED'
          ? 'closed'
          : summary.registrationManualState === 'HIDDEN'
            ? 'hidden'
            : undefined,
    autoReminderAt: summary.reminderAt,
    timezone: summary.timezone,
    branding: {
      ...DEFAULT_BRANDING,
      primary: summary.primaryColor ?? DEFAULT_BRANDING.primary,
      background: summary.backgroundColor ?? DEFAULT_BRANDING.background,
      text: summary.textColor ?? DEFAULT_BRANDING.text,
      accent: summary.accentColor ?? DEFAULT_BRANDING.accent,
      bodyFont: summary.fontFamily ?? DEFAULT_BRANDING.bodyFont,
      headingFont: summary.headingFontFamily ?? DEFAULT_BRANDING.headingFont,
      logo: summary.logoS3Key ? assetUrl(summary.id, 'logo', summary.logoS3Key) : undefined,
    },
    createdAt: summary.createdAt ?? new Date().toISOString(),
    draftPreview: draftPreview || undefined,
  }
}
